import { apiService } from "@/services/api";
import { createCrudStore } from "@/store/crudStore";
import db from "@/services/offlineDb";
import { enqueueOp } from "@/services/offlineQueue";
import {
  normalizarCaravana,
  normalizarMapaCaravanas,
} from "@/utils/caravanaHelper";

// Crear el store base usando crudStore
const vacasStore = createCrudStore("grilla_vaca");

// TTL para cache de pendientes (30 minutos en ms)
// Los flags se recalculan al hacer mutaciones y por el scheduler cada 12hs,
// por lo que un cache largo es seguro y mejora mucho la performance.
const PENDIENTES_CACHE_TTL = 30 * 60 * 1000;

const DEFAULT_CONTEOS_PENDIENTES = {
  recria: {
    destete: 0,
    guachera_recria2: 0,
    recria2_3: 0,
    recria3_4: 0,
    recria4_vaq: 0,
    total: 0,
  },
  tambo: { cola_punta: 0, punta_cola: 0, total: 0 },
  servicio: {
    tacto_preservicio: 0,
    pendiente_inseminacion: 0,
    observacion_celo: 0,
    tacto_prenez: 0,
    tacto_postparto: 0,
    total: 0,
  },
  secas_partos: {
    pendiente_secado: 0,
    pendiente_preparto: 0,
    partos: 0,
    total: 0,
  },
  salud: {
    enfermas: 0,
    en_tratamiento: 0,
    vacunacion_recria: 0,
    vacunacion_tambo: 0,
    total: 0,
  },
  ventas: { machos: 0, por_edad: 0, estado_corporal: 0, total: 0 },
  estadisticas_corporal: {},
  reproduccion: 0,
};

const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const firstNumber = (...values) => {
  const found = values.find((value) => value !== undefined && value !== null);
  return toNumber(found);
};

const sumGroup = (group) =>
  Object.entries(group || {}).reduce((total, [key, value]) => {
    return key === "total" ? total : total + toNumber(value);
  }, 0);

const normalizeConteosPendientes = (raw = {}) => {
  const source = raw?.data || raw || {};
  const servicioSource = source.servicio || source.reproduccion || {};
  const secasSource =
    source.secas_partos || source.partos || source.reproduccion || {};
  const saludSource = source.salud || {};
  const ventasSource = source.ventas || {};
  const recriaSource = source.recria || {};
  const tamboSource = source.tambo || {};

  const conteos = {
    recria: {
      destete: firstNumber(recriaSource.destete),
      guachera_recria2: firstNumber(
        recriaSource.guachera_recria2,
        recriaSource.gua_rcr2
      ),
      recria2_3: firstNumber(recriaSource.recria2_3, recriaSource.rcr2_3),
      recria3_4: firstNumber(recriaSource.recria3_4, recriaSource.rcr3_4),
      recria4_vaq: firstNumber(recriaSource.recria4_vaq, recriaSource.rcr4_vaq),
      total: firstNumber(
        recriaSource.total,
        typeof source.recria === "number" ? source.recria : undefined
      ),
    },
    tambo: {
      cola_punta: firstNumber(tamboSource.cola_punta),
      punta_cola: firstNumber(tamboSource.punta_cola),
      total: firstNumber(
        tamboSource.total,
        typeof source.tambo === "number" ? source.tambo : undefined
      ),
    },
    servicio: {
      tacto_preservicio: firstNumber(servicioSource.tacto_preservicio),
      pendiente_inseminacion: firstNumber(
        servicioSource.pendiente_inseminacion,
        servicioSource.inseminacion
      ),
      observacion_celo: firstNumber(
        servicioSource.observacion_celo,
        servicioSource.pendiente_observacion_celo,
        servicioSource.celos_prenez
      ),
      tacto_prenez: firstNumber(
        servicioSource.tacto_prenez,
        servicioSource.tacto_preniez,
        servicioSource.confirmacion_preniez,
        servicioSource.pendiente_confirmacion_preniez
      ),
      tacto_postparto: firstNumber(servicioSource.tacto_postparto),
      total: firstNumber(servicioSource.total),
    },
    secas_partos: {
      pendiente_secado: firstNumber(
        secasSource.pendiente_secado,
        secasSource.secado
      ),
      pendiente_preparto: firstNumber(
        secasSource.pendiente_preparto,
        secasSource.preparto
      ),
      partos: firstNumber(
        secasSource.partos,
        secasSource.parto,
        secasSource.pendiente_parto
      ),
      total: firstNumber(secasSource.total),
    },
    salud: {
      enfermas: firstNumber(saludSource.enfermas),
      en_tratamiento: firstNumber(saludSource.en_tratamiento),
      vacunacion_recria: firstNumber(
        saludSource.vacunacion_recria,
        saludSource.vacunas_recria,
        saludSource.vacunas_pendientes_recria
      ),
      vacunacion_tambo: firstNumber(
        saludSource.vacunacion_tambo,
        saludSource.vacunas_tambo,
        saludSource.vacunas_pendientes_tambo,
        saludSource.vacunacion
      ),
      total: firstNumber(
        saludSource.total,
        typeof source.salud === "number" ? source.salud : undefined
      ),
    },
    ventas: {
      machos: firstNumber(ventasSource.machos),
      por_edad: firstNumber(ventasSource.por_edad, ventasSource.edad),
      estado_corporal: firstNumber(
        ventasSource.estado_corporal,
        ventasSource.condicion_corporal
      ),
      total: firstNumber(
        ventasSource.total,
        typeof source.ventas === "number" ? source.ventas : undefined
      ),
    },
    estadisticas_corporal: source.estadisticas_corporal || {},
  };

  conteos.recria.total ||= sumGroup(conteos.recria);
  conteos.tambo.total ||= sumGroup(conteos.tambo);
  conteos.servicio.total ||= sumGroup(conteos.servicio);
  conteos.secas_partos.total ||= sumGroup(conteos.secas_partos);
  conteos.salud.total ||= sumGroup(conteos.salud);
  conteos.ventas.total ||= sumGroup(conteos.ventas);
  conteos.reproduccion = firstNumber(
    source.reproduccion_total,
    typeof source.reproduccion === "number" ? source.reproduccion : undefined,
    conteos.servicio.total + conteos.secas_partos.total
  );

  return conteos;
};

const hasGranularConteos = (conteos) => {
  const groups = [
    "recria",
    "tambo",
    "servicio",
    "secas_partos",
    "salud",
    "ventas",
  ];
  return groups.some((groupName) =>
    Object.entries(conteos[groupName] || {}).some(
      ([key, value]) => key !== "total" && toNumber(value) > 0
    )
  );
};

const hasGranularConteosInGroup = (group) =>
  Object.entries(group || {}).some(
    ([key, value]) => key !== "total" && toNumber(value) > 0
  );

const preferFallbackGroupWhenNeeded = (currentGroup, fallbackGroup) => {
  const currentTotal = toNumber(currentGroup?.total) || sumGroup(currentGroup);
  const fallbackTotal =
    toNumber(fallbackGroup?.total) || sumGroup(fallbackGroup);

  if (!hasGranularConteosInGroup(currentGroup) && fallbackTotal > 0) {
    return fallbackGroup;
  }

  if (fallbackTotal > currentTotal) {
    return fallbackGroup;
  }

  return currentGroup;
};

const buildConteosFromPendientes = (animales = []) => {
  const conteos = normalizeConteosPendientes(DEFAULT_CONTEOS_PENDIENTES);

  animales.forEach((v) => {
    const currentLote = v.current_lote || {};
    const orden = currentLote.orden;
    const codigo = (currentLote.codigo || "").toUpperCase();
    const hasRecriaPend = v.pendiente_cambio || v.cambio_vencido;
    const isCambioLacteo = v.pendiente_cambio_lacteo;
    const cantCrias = v.cantidad_crias || 0;
    const isOrdene = ["PUN", "COL", "VAQ-OR", "PUNTA", "COLA"].includes(codigo);
    const estadosArray = Array.isArray(v.estado_actual) ? v.estado_actual : [];
    const hasUnconfirmedInsem = estadosArray.some(
      (estado) =>
        typeof estado === "string" &&
        estado.toLowerCase().includes("inseminada")
    );

    if (hasRecriaPend && orden === 1) conteos.recria.destete += 1;
    if (hasRecriaPend && orden === 2) conteos.recria.guachera_recria2 += 1;
    if (hasRecriaPend && orden === 3) conteos.recria.recria2_3 += 1;
    if (hasRecriaPend && orden === 4) conteos.recria.recria3_4 += 1;
    if (hasRecriaPend && orden === 5) conteos.recria.recria4_vaq += 1;

    if (isCambioLacteo && (codigo === "COL" || codigo === "COLA"))
      conteos.tambo.cola_punta += 1;
    if (isCambioLacteo && (codigo === "PUN" || codigo === "PUNTA"))
      conteos.tambo.punta_cola += 1;

    const isPrenada =
      estadosArray.some(
        (e) => typeof e === "string" && e.toLowerCase().includes("preñada")
      ) || !!v.dias_prenada;

    if (
      v.pendiente_tacto &&
      !hasUnconfirmedInsem &&
      !isPrenada &&
      cantCrias < 1 &&
      !isOrdene
    )
      conteos.servicio.tacto_preservicio += 1;
    if (
      v.pendiente_tacto &&
      !hasUnconfirmedInsem &&
      !isPrenada &&
      (cantCrias >= 1 || isOrdene)
    )
      conteos.servicio.tacto_postparto += 1;
    if (v.pendiente_inseminacion) conteos.servicio.pendiente_inseminacion += 1;
    if (v.pendiente_observacion_celo) conteos.servicio.observacion_celo += 1;
    if (
      v.pendiente_confirmacion_preniez ||
      (v.pendiente_tacto && (hasUnconfirmedInsem || isPrenada))
    )
      conteos.servicio.tacto_prenez += 1;

    if (v.pendiente_secado) conteos.secas_partos.pendiente_secado += 1;
    if (v.pendiente_preparto) conteos.secas_partos.pendiente_preparto += 1;
    if (v.pendiente_parto) conteos.secas_partos.partos += 1;

    if (v.enferma && !v.en_tratamiento) conteos.salud.enfermas += 1;
    if (v.enferma && v.en_tratamiento) conteos.salud.en_tratamiento += 1;
    if (
      Array.isArray(v.vacunas_pendientes) &&
      v.vacunas_pendientes.length > 0 &&
      orden >= 1 &&
      orden <= 5
    ) {
      conteos.salud.vacunacion_recria += 1;
    }
    if (
      Array.isArray(v.vacunas_pendientes) &&
      v.vacunas_pendientes.length > 0 &&
      (!orden || orden >= 6)
    ) {
      conteos.salud.vacunacion_tambo += 1;
    }

    if (v.pendiente_venta && v.sexo === "Macho") conteos.ventas.machos += 1;
    if (v.pendiente_venta && v.sexo === "Hembra") conteos.ventas.por_edad += 1;
    const estadoCorporal = parseFloat(v.estado_corporal_actual);
    if (
      estadoCorporal !== 0 &&
      (estadoCorporal <= 2.5 || estadoCorporal >= 4.0)
    ) {
      conteos.ventas.estado_corporal += 1;
    }
  });

  conteos.recria.total = sumGroup(conteos.recria);
  conteos.tambo.total = sumGroup(conteos.tambo);
  conteos.servicio.total = sumGroup(conteos.servicio);
  conteos.secas_partos.total = sumGroup(conteos.secas_partos);
  conteos.salud.total = sumGroup(conteos.salud);
  conteos.ventas.total = sumGroup(conteos.ventas);
  conteos.reproduccion = conteos.servicio.total + conteos.secas_partos.total;

  return conteos;
};

export default {
  ...vacasStore,
  state: {
    criaPartoPendiente: null,
    ...vacasStore.state,
    animalesResumen: [],
    pendientesCambio: [],
    pendientesPorCategoria: {},
    pendientesCacheTimestampPorCategoria: {},
    pendientesCacheTimestamp: null, // Timestamp de última carga
    conteosPendientes: normalizeConteosPendientes(DEFAULT_CONTEOS_PENDIENTES),
    cargandoConteosPendientes: false,
    animalesVendidos: [],
    animalesMuertos: [],
    animalesAbortos: [],
    paginacionMuertos: {
      total: 0,
      pagina_actual: 1,
      total_paginas: 1,
      limite: 50,
      siguiente_pagina: null,
      anterior_pagina: null,
    },
    paginacionAbortos: {
      total: 0,
      pagina_actual: 1,
      total_paginas: 1,
      limite: 50,
      siguiente_pagina: null,
      anterior_pagina: null,
    },
    paginacionVendidos: {
      total: 0,
      pagina_actual: 1,
      total_paginas: 1,
      limite: 15,
      siguiente_pagina: null,
      anterior_pagina: null,
    },
    paginacion: {
      total: 0,
      pagina_actual: 1,
      total_paginas: 1,
      limite: 15,
      siguiente_pagina: null,
      anterior_pagina: null,
    },
    paginacionPendientes: {
      total: 0,
      pagina_actual: 1,
      total_paginas: 1,
      limite: 10,
      siguiente_pagina: null,
      anterior_pagina: null,
    },
  },

  getters: {
    ...vacasStore.getters,
    criaPartoPendiente: (state) => state.criaPartoPendiente,
    vacas(state) {
      return state.animalesResumen;
    },
    vendidos(state) {
      return state.animalesVendidos;
    },
    paginacionVendidos(state) {
      return state.paginacionVendidos;
    },
    paginacion(state) {
      return state.paginacion;
    },
    paginacionPendientes(state) {
      return state.paginacionPendientes;
    },
    pendientesCambio(state) {
      return state.pendientesCambio;
    },
    conteosPendientes(state) {
      return state.conteosPendientes;
    },
    cargandoConteosPendientes(state) {
      return state.cargandoConteosPendientes;
    },
    pendientesCacheTimestamp(state) {
      return state.pendientesCacheTimestamp;
    },
    isPendientesCacheValid(state) {
      if (!state.pendientesCacheTimestamp) return false;
      return Date.now() - state.pendientesCacheTimestamp < PENDIENTES_CACHE_TTL;
    },
    muertos(state) {
      return state.animalesMuertos;
    },
    abortos(state) {
      return state.animalesAbortos;
    },
    paginacionMuertos(state) {
      return state.paginacionMuertos;
    },
    paginacionAbortos(state) {
      return state.paginacionAbortos;
    },
  },

  mutations: {
    ...vacasStore.mutations,
    setCriaPartoPendiente(state, data) {
      state.criaPartoPendiente = data;
    },
    setAnimalesResumen(state, data) {
      state.animalesResumen = data;
    },
    setPaginacion(state, paginacion) {
      state.paginacion = paginacion;
    },
    setAnimalesVendidos(state, data) {
      state.animalesVendidos = data;
    },
    setPaginacionVendidos(state, paginacion) {
      state.paginacionVendidos = paginacion;
    },
    setPaginacionPendientes(state, paginacion) {
      state.paginacionPendientes = paginacion;
    },
    setPendientesCambio(state, data) {
      state.pendientesCambio = data;
    },
    setPendientesPorCategoria(state, { categoria, datos }) {
      state.pendientesPorCategoria = {
        ...state.pendientesPorCategoria,
        [categoria]: datos,
      };
    },
    appendPendientesPorCategoria(state, { categoria, datos }) {
      const existing = state.pendientesPorCategoria[categoria] || [];
      state.pendientesPorCategoria = {
        ...state.pendientesPorCategoria,
        [categoria]: [...existing, ...datos],
      };
    },
    setPendientesCacheTimestampPorCategoria(state, { categoria, timestamp }) {
      state.pendientesCacheTimestampPorCategoria = {
        ...state.pendientesCacheTimestampPorCategoria,
        [categoria]: timestamp,
      };
    },
    setPendientesCacheTimestamp(state, timestamp) {
      state.pendientesCacheTimestamp = timestamp;
    },
    invalidatePendientesCachePorCategoria(state) {
      state.pendientesCacheTimestampPorCategoria = {};
    },
    invalidatePendientesCache(state) {
      state.pendientesCacheTimestamp = null;
      state.pendientesCacheTimestampPorCategoria = {};
    },
    setAnimalesMuertos(state, data) {
      state.animalesMuertos = data;
    },
    setAnimalesAbortos(state, data) {
      state.animalesAbortos = data;
    },
    setPaginacionMuertos(state, pag) {
      state.paginacionMuertos = pag;
    },
    setPaginacionAbortos(state, pag) {
      state.paginacionAbortos = pag;
    },
    setConteosPendientes(state, conteos) {
      state.conteosPendientes = normalizeConteosPendientes({
        ...state.conteosPendientes,
        ...conteos,
      });
    },
    setCargandoConteosPendientes(state, status) {
      state.cargandoConteosPendientes = status;
    },
  },

  actions: {
    ...vacasStore.actions,

    getAllGrillaVaca: vacasStore.actions.getAllStates,
    getGrillaVacaById: vacasStore.actions.getStateById,
    deleteGrillaVaca: vacasStore.actions.delete,

    // postGrillaVaca y patchGrillaVaca invalidan el cache de pendientes
    // ya que crean/modifican animales que afectan los flags
    async postGrillaVaca({ dispatch, commit }, payload) {
      if (!navigator.onLine) {
        await enqueueOp("animales", "create", payload, "/grilla_vaca", "POST");
        const localAnimal = {
          ...payload,
          id: Date.now(),
          _offline: true,
        };
        await db.animales.put(localAnimal).catch(() => {});
        commit("addItem", localAnimal);
        return { status: 201, data: localAnimal };
      }
      return await dispatch("executeApiCall", {
        apiCallFunction: () => apiService.create("grilla_vaca", payload),
        onSuccessCallback: (commit, data) => {
          commit("addItem", data);
          commit("invalidatePendientesCache");
        },
        errorMessage: "Error creando animal",
      });
    },

    async patchGrillaVaca({ dispatch, commit }, payload) {
      if (!navigator.onLine) {
        await enqueueOp(
          "animales",
          "update",
          payload,
          `/grilla_vaca/${payload.id}`,
          "PATCH"
        );
        const local = await db.animales.where("id").equals(payload.id).first();
        if (local) {
          await db.animales.update(local.localId, payload).catch(() => {});
        }
        commit("updateItem", payload);
        commit("invalidatePendientesCache");
        return { status: 200, data: payload };
      }
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.update("grilla_vaca", payload.id, payload),
        onSuccessCallback: (commit, data) => {
          commit("updateItem", data);
          commit("invalidatePendientesCache");
        },
        errorMessage: "Error actualizando animal",
      });
    },

    async getVacas(
      { dispatch, commit },
      {
        pagina = 1,
        limite = 15,
        sexo = null,
        lote_id = null,
        solo_alertas = false,
      } = {}
    ) {
      try {
        return await dispatch("executeApiCall", {
          apiCallFunction: () => {
            // Construir URL con parámetros
            const params = new URLSearchParams();
            params.append("pagina", pagina);
            params.append("limite", limite);

            // Solo agregar sexo si no es "Todos" o null
            if (sexo && sexo.toLowerCase() !== "todos") {
              // Convertir a formato del backend (macho/hembra o m/h)
              const sexoParam =
                sexo.toLowerCase() === "macho"
                  ? "macho"
                  : sexo.toLowerCase() === "hembra"
                  ? "hembra"
                  : null;
              if (sexoParam) {
                params.append("sexo", sexoParam);
              }
            }

            // Agregar lote_id si está definido
            if (lote_id !== null) {
              params.append("lote_id", lote_id);
            }

            // Agregar solo_alertas si está activo
            if (solo_alertas) {
              params.append("solo_alertas", "true");
            }

            return apiService.getCustom(
              `grilla_vaca/resumen?${params.toString()}`
            );
          },
          onSuccessCallback: (commit, response) => {
            const {
              datos,
              total,
              pagina_actual,
              total_paginas,
              limite,
              siguiente_pagina,
              anterior_pagina,
            } = response;

            commit("setAnimalesResumen", datos || []);
            commit("setPaginacion", {
              total: total || 0,
              pagina_actual: pagina_actual || 1,
              total_paginas: total_paginas || 1,
              limite: limite || 15,
              siguiente_pagina: siguiente_pagina || null,
              anterior_pagina: anterior_pagina || null,
            });

            // Guardar en Dexie para offline
            if (datos && datos.length > 0) {
              db.animales.bulkPut(datos).catch(() => {});
            }

            
          },
          errorMessage: "Error obteniendo resumen de vacas",
        });
      } catch (err) {
        // Offline fallback
        if (!navigator.onLine) {
          const local = await db.animales.toArray();
          const lotesLocal = await db.lotes.toArray();
          const lotesMap = new Map();
          for (const l of lotesLocal) {
            const lid = l.lote_id || l.id;
            if (lid) lotesMap.set(lid, l.nombre_lote || l.nombre);
          }

          // Enriquecer lote_actual si no lo tiene
          let filtrados = local.map((a) => {
            const lid = a.lote_id || a.lote_actual?.lote_id;
            const nombre =
              a.lote_actual?.nombre_lote || (lid ? lotesMap.get(lid) : null);
            return {
              ...a,
              lote_id: lid,
              lote_nombre: nombre || (lid ? `Lote #${lid}` : null),
              lote_actual: lid
                ? { lote_id: lid, nombre_lote: nombre || `Lote #${lid}` }
                : a.lote_actual,
            };
          });

          // Filtrar activos (no vendidos, no muertos)
          filtrados = filtrados.filter(
            (a) => !a.vendido && a.estado_excepcion !== "muerto"
          );

          // Filtro por sexo
          if (sexo && sexo.toLowerCase() !== "todos") {
            const s = sexo.toLowerCase().trim();
            filtrados = filtrados.filter(
              (a) => String(a.sexo || "").toLowerCase().trim() === s
            );
          }

          // Filtro por lote_id
          if (lote_id !== null && lote_id !== undefined && lote_id !== "") {
            filtrados = filtrados.filter(
              (a) => (a.lote_id || a.lote_actual?.lote_id) == lote_id
            );
          }

          // Filtro por solo_alertas
          if (solo_alertas) {
            filtrados = filtrados.filter(
              (a) =>
                a.pendiente_cambio ||
                a.pendiente_inseminacion ||
                a.pendiente_tacto ||
                a.enferma ||
                a.en_tratamiento
            );
          }

          const total = filtrados.length;
          const pagActual = parseInt(pagina, 10) || 1;
          const lim = parseInt(limite, 10) || 15;
          const inicio = (pagActual - 1) * lim;
          const paginados = filtrados.slice(inicio, inicio + lim);
          const totalPaginas = Math.ceil(total / lim) || 1;

          commit("setAnimalesResumen", paginados);
          commit("setPaginacion", {
            total,
            pagina_actual: pagActual,
            total_paginas: totalPaginas,
            limite: lim,
            siguiente_pagina: pagActual < totalPaginas ? pagActual + 1 : null,
            anterior_pagina: pagActual > 1 ? pagActual - 1 : null,
          });
          return {
            datos: paginados,
            total,
            pagina_actual: pagActual,
            total_paginas: totalPaginas,
          };
        }
        throw err;
      }
    },

    async buscarPorCaravana({ dispatch, commit }, { caravana = null } = {}) {
      if (!navigator.onLine) {
        const local = await db.animales.toArray();
        const lotesLocal = await db.lotes.toArray();
        const lotesMap = new Map();
        for (const l of lotesLocal) {
          const lid = l.lote_id || l.id;
          if (lid) lotesMap.set(lid, l.nombre_lote || l.nombre);
        }

        const texto =
          caravana === null || caravana === undefined
            ? ""
            : String(caravana).trim().toLowerCase();
        const norm = normalizarCaravana(texto);

        let filtrados = local.map((a) => {
          const lid = a.lote_id || a.lote_actual?.lote_id;
          const nombre =
            a.lote_actual?.nombre_lote || (lid ? lotesMap.get(lid) : null);
          return {
            ...a,
            lote_id: lid,
            lote_nombre: nombre || (lid ? `Lote #${lid}` : null),
            lote_actual: lid
              ? { lote_id: lid, nombre_lote: nombre || `Lote #${lid}` }
              : a.lote_actual,
          };
        });

        filtrados = filtrados.filter(
          (a) => !a.vendido && a.estado_excepcion !== "muerto"
        );

        if (texto !== "") {
          filtrados = filtrados.filter(
            (a) =>
              String(a.caravana || "")
                .toLowerCase()
                .includes(texto) ||
              (norm && normalizarCaravana(a.caravana) === norm)
          );
        }

        commit("setAnimalesResumen", filtrados);
        commit("setPaginacion", {
          total: filtrados.length,
          pagina_actual: 1,
          total_paginas: 1,
          limite: filtrados.length || 15,
          siguiente_pagina: null,
          anterior_pagina: null,
        });
        return filtrados;
      }
      return await dispatch("executeApiCall", {
        apiCallFunction: () => {
          const params = new URLSearchParams();
          // La caravana es texto (`AR-101`): se manda tal cual, el backend la normaliza.
          const texto =
            caravana === null || caravana === undefined
              ? ""
              : String(caravana).trim();
          if (texto !== "") {
            params.append("caravana", texto);
          }

          return apiService.getCustom(
            `grilla_vaca/buscar_por_caravana?${params.toString()}`
          );
        },
        onSuccessCallback: (commit, response) => {
          const datos = Array.isArray(response)
            ? response
            : response.datos || [];

          commit("setAnimalesResumen", datos);

          commit("setPaginacion", {
            total: datos.length,
            pagina_actual: 1,
            total_paginas: 1,
            limite: datos.length,
            siguiente_pagina: null,
            anterior_pagina: null,
          });

          
        },
        errorMessage: "Error buscando por caravana",
      });
    },

    /**
     * Caravanas que ya existen en el rodeo. Manda las caravanas normalizadas (así `ar-1`
     * y `0052` encuentran a `AR-1` y `52`) y devuelve la respuesta con `data` como mapa
     * `caravana normalizada → id`.
     */
    async validarCaravanas(_context, caravanas) {
      const normalizadas = [
        ...new Set(
          (caravanas || []).map(normalizarCaravana).filter((c) => c !== null)
        ),
      ];
      if (normalizadas.length === 0) return { data: {} };
      const response = await apiService.postCustom(
        "grilla_vaca/validar_caravanas",
        { caravanas: normalizadas }
      );
      return {
        ...response,
        data: normalizarMapaCaravanas(response?.data),
      };
    },

    async getPendientesCambio(
      { dispatch, commit },
      {
        pagina = 1,
        limite = 10,
        categoria = null,
        subcategoria = null,
        append = false,
      } = {}
    ) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () => {
          let url = `grilla_vaca/pendientes?pagina=${pagina}&limite=${limite}`;
          if (categoria) {
            url += `&categoria=${categoria}`;
          }
          if (subcategoria) {
            url += `&subcategoria=${subcategoria}`;
          }
          return apiService.getCustom(url);
        },
        onSuccessCallback: (commit, response) => {
          const {
            datos,
            total,
            pagina_actual,
            total_paginas,
            limite: responseLimite,
            siguiente_pagina,
            anterior_pagina,
          } = response;

          commit("setPendientesCambio", datos || []);

          if (!categoria) {
            commit("setPendientesCacheTimestamp", Date.now());
            commit("invalidatePendientesCachePorCategoria");
          } else {
            if (append) {
              commit("appendPendientesPorCategoria", {
                categoria,
                datos: datos || [],
              });
            } else {
              commit("setPendientesPorCategoria", {
                categoria,
                datos: datos || [],
              });
            }
            commit("setPendientesCacheTimestampPorCategoria", {
              categoria,
              timestamp: Date.now(),
            });
          }

          commit("setPaginacionPendientes", {
            total: total || 0,
            pagina_actual: pagina_actual || 1,
            total_paginas: total_paginas || 1,
            limite: responseLimite || 10,
            siguiente_pagina: siguiente_pagina || null,
            anterior_pagina: anterior_pagina || null,
          });
        },
        errorMessage: "Error obteniendo vacas pendientes de cambio",
      });
    },

    async fetchConteosPendientes({ commit }) {
      commit("setCargandoConteosPendientes", true);
      try {
        // ✅ OPTIMIZADO: Un único endpoint SQL en lugar de 5 requests secuenciales.
        // El nuevo endpoint /pendientes/conteos usa COUNT condicional directo en DB,
        // sin cargar los animales en Python. Es O(1) queries vs O(5 * N animales).
        const response = await apiService.getCustom(
          "grilla_vaca/pendientes/conteos"
        );
        let conteos = normalizeConteosPendientes(response);

        const saludSinVacunas =
          !toNumber(conteos.salud?.vacunacion_recria) &&
          !toNumber(conteos.salud?.vacunacion_tambo);

        if (
          !hasGranularConteos(conteos) ||
          !hasGranularConteosInGroup(conteos.salud) ||
          saludSinVacunas
        ) {
          const pendientesResponse = await apiService.getCustom(
            "grilla_vaca/pendientes?pagina=1&limite=1000"
          );
          const pendientes = Array.isArray(pendientesResponse)
            ? pendientesResponse
            : pendientesResponse?.datos ||
              pendientesResponse?.data?.datos ||
              [];
          const fallbackConteos = buildConteosFromPendientes(pendientes);

          if (!hasGranularConteos(conteos)) {
            conteos = fallbackConteos;
          } else {
            conteos = {
              ...conteos,
              salud: preferFallbackGroupWhenNeeded(
                conteos.salud,
                fallbackConteos.salud
              ),
            };
          }
        }

        commit("setConteosPendientes", conteos);
      } catch (error) {
        if (!navigator.onLine) {
          try {
            const local = await db.animales.toArray();
            const pendientes = local.filter(
              (a) =>
                a.pendiente_cambio ||
                a.pendiente_inseminacion ||
                a.pendiente_tacto
            );
            commit(
              "setConteosPendientes",
              buildConteosFromPendientes(pendientes)
            );
          } catch {
            // Silencioso
          }
        } else {
          try {
            const pendientesResponse = await apiService.getCustom(
              "grilla_vaca/pendientes?pagina=1&limite=1000"
            );
            const pendientes = Array.isArray(pendientesResponse)
              ? pendientesResponse
              : pendientesResponse?.datos ||
                pendientesResponse?.data?.datos ||
                [];
            commit(
              "setConteosPendientes",
              buildConteosFromPendientes(pendientes)
            );
          } catch (fallbackError) {
            
          }
        }
      } finally {
        commit("setCargandoConteosPendientes", false);
      }
    },

    /**
     * Obtener pendientes con cache - evita llamadas innecesarias al API
     * @param {boolean} forceRefresh - Forzar recarga ignorando cache
     */
    async getPendientesCambioConCache(
      { dispatch, getters, state },
      {
        pagina = 1,
        limite = 100,
        categoria = null,
        subcategoria = null,
        forceRefresh = false,
      } = {}
    ) {
      const catKey = categoria || "todas";
      const timestamp = categoria
        ? state.pendientesCacheTimestampPorCategoria[categoria]
        : state.pendientesCacheTimestamp;
      const isCacheValid =
        timestamp && Date.now() - timestamp < PENDIENTES_CACHE_TTL;
      const cachedData = categoria
        ? state.pendientesPorCategoria[categoria]
        : state.pendientesCambio;

      // Si el cache es válido y no se fuerza recarga (y no hay subcategoría), retornar datos existentes
      if (
        !forceRefresh &&
        isCacheValid &&
        cachedData !== undefined &&
        !subcategoria
      ) {
        
        return cachedData;
      }

      // Cache expirado o forzado: cargar desde API
      await dispatch("getPendientesCambio", {
        pagina,
        limite,
        categoria,
        subcategoria,
      });

      // Retornar los datos actualizados del state
      return categoria
        ? state.pendientesPorCategoria[categoria]
        : state.pendientesCambio;
    },

    async getPartosByAnimalId({ dispatch }, animalId) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(`grilla_vaca/partos/${animalId}`),
        onSuccessCallback: (commit, response) => {
          
          return response;
        },
        errorMessage: "Error obteniendo historial de partos",
      });
    },

    async patchParto({ dispatch }, { partoId, payload }) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.patchCustom(`grilla_vaca/partos/${partoId}`, payload),
        onSuccessCallback: (commit, response) => {
          
          return response;
        },
        errorMessage: "Error actualizando el parto",
      });
    },

    async getEventosByAnimalId({ dispatch }, { animalId, limite = 50 }) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(
            `grilla_vaca/eventos/${animalId}?limite=${limite}`
          ),
        onSuccessCallback: (commit, response) => {
          
          return response;
        },
        errorMessage: "Error obteniendo historial de eventos",
      });
    },

    async getVendidos({ dispatch }, { pagina = 1, limite = 15 } = {}) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(
            `grilla_vaca/vendidos?pagina=${pagina}&limite=${limite}`
          ),
        onSuccessCallback: (commit, response) => {
          const datos = Array.isArray(response)
            ? response
            : response.datos || [];

          if (response.total !== undefined) {
            const {
              total,
              pagina_actual,
              total_paginas,
              limite: responseLimite,
              siguiente_pagina,
              anterior_pagina,
            } = response;

            commit("setPaginacionVendidos", {
              total: total || 0,
              pagina_actual: pagina_actual || 1,
              total_paginas: total_paginas || 1,
              limite: responseLimite || 15,
              siguiente_pagina: siguiente_pagina || null,
              anterior_pagina: anterior_pagina || null,
            });
          } else {
            commit("setPaginacionVendidos", {
              total: datos.length,
              pagina_actual: 1,
              total_paginas: 1,
              limite: datos.length,
              siguiente_pagina: null,
              anterior_pagina: null,
            });
          }

          commit("setAnimalesVendidos", datos);
          
        },
        errorMessage: "Error obteniendo historial de vendidos",
      });
    },

    async toggleEnferma(
      { dispatch },
      { animalId, enferma, detalle = null, enfermedad = null }
    ) {
      if (!navigator.onLine) {
        await enqueueOp(
          "animales",
          "update",
          { enferma, detalle, enfermedad },
          `/grilla_vaca/setear_enferma/${animalId}`,
          "POST"
        );
        const local = await db.animales.where("id").equals(animalId).first();
        if (local) {
          await db.animales.update(local.localId, {
            enferma,
            detalle_enfermedad: detalle || enfermedad,
          });
        }
        return {
          status: 200,
          data: { enferma, detalle, enfermedad, _offline: true },
        };
      }
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(`grilla_vaca/setear_enferma/${animalId}`, {
            enferma: enferma,
            detalle: detalle,
            enfermedad: enfermedad,
          }),
        onSuccessCallback: async (commit, response) => {
          
          // Opcional: recargar la lista de vacas para reflejar el cambio
          // await dispatch("getVacas", { pagina: 1, limite: 15 });
        },
        errorMessage: "Error al actualizar estado de enferma",
      });
    },

    async marcarFallecido({ dispatch, commit }, { animalId, payload }) {
      if (!navigator.onLine) {
        await enqueueOp(
          "animales",
          "update",
          payload || {},
          `/grilla_vaca/fallecimiento/${animalId}`,
          "POST"
        );
        const local = await db.animales.where("id").equals(animalId).first();
        if (local) {
          await db.animales.update(local.localId, {
            estado_excepcion: "muerto",
          });
        }
        return {
          status: 200,
          data: { detail: "Fallecimiento registrado offline", _offline: true },
        };
      }
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(
            `grilla_vaca/fallecimiento/${animalId}`,
            payload || {}
          ),
        onSuccessCallback: (commit) => {
          commit("invalidatePendientesCache");
          
        },
        errorMessage: "Error al marcar el animal como fallecido",
      });
    },

    async movimientoManual({ dispatch, commit }, { animalId, payload }) {
      if (!navigator.onLine) {
        await enqueueOp(
          "animales",
          "update",
          payload,
          `/grilla_vaca/movimiento_manual/${animalId}`,
          "POST"
        );
        const local = await db.animales.where("id").equals(animalId).first();
        if (local) {
          const nuevoLoteId = payload.lote_id || payload.lote_destino_id;
          await db.animales
            .update(local.localId, {
              lote_id: nuevoLoteId,
              pendiente_cambio: false,
            })
            .catch(() => {});
        }
        commit("invalidatePendientesCache");
        await dispatch("getVacas", { pagina: 1, limite: 15 });
        return {
          status: 200,
          data: { detail: "Movimiento registrado offline", _offline: true },
        };
      }
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(
            `grilla_vaca/movimiento_manual/${animalId}`,
            payload
          ),
        onSuccessCallback: async (commit) => {
          commit("invalidatePendientesCache");
          await dispatch("getVacas", { pagina: 1, limite: 15 });
        },
        errorMessage: "Error al ejecutar movimiento manual",
      });
    },

    async crearTratamiento({ dispatch, commit }, { animalId, payload }) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(
            `grilla_vaca/${animalId}/tratamientos`,
            payload
          ),
        onSuccessCallback: async (commit) => {
          commit("invalidatePendientesCache");
        },
        errorMessage: "Error al registrar el tratamiento",
      });
    },

    async previewExcel({ dispatch }, file) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("limit", "200");

      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom("/grilla_vaca/upload-excel/preview", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          }),
        onSuccessCallback: (commit, data) => {
          
          return data;
        },
        errorMessage: "Error analizando el archivo Excel",
      });
    },

    async importExcel(
      { dispatch, commit },
      {
        uploadId,
        autoGenerateCaravana = false,
        chunkSize = 200,
        enFeedlot = false,
      }
    ) {
      if (!uploadId) {
        throw new Error("upload_id es requerido para importar");
      }

      const formData = new FormData();
      formData.append("upload_id", uploadId);

      const url = `/grilla_vaca/upload-excel/commit?auto_generate_caravana=${autoGenerateCaravana}&chunk_size=${chunkSize}&en_feedlot=${enFeedlot}`;

      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(url, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          }),
        onSuccessCallback: (commit, data) => {
          commit("invalidatePendientesCache");
          
          return data;
        },
        errorMessage: "Error importando los datos del Excel",
      });
    },

    async previewLitrosExcel({ dispatch }, file) {
      const formData = new FormData();
      formData.append("file", file);

      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(
            "/grilla_vaca/upload-excel/preview_litros",
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          ),
        errorMessage: "Error analizando el archivo Excel de litros",
      });
    },

    async commitLitrosExcel(
      { dispatch },
      {
        uploadId,
        autoCreateAnimal = false,
        evaluarCambiosLote = true,
        fecha = null,
      }
    ) {
      const formData = new URLSearchParams();
      formData.append("upload_id", uploadId);

      const params = new URLSearchParams();
      params.append("auto_create_animal", autoCreateAnimal);
      params.append("evaluar_cambios_lote", evaluarCambiosLote);
      if (fecha) {
        params.append("fecha_medicion", fecha);
      }

      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(
            `/grilla_vaca/upload-excel/commit_litros?${params.toString()}`,
            formData.toString(),
            {
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
              },
            }
          ),
        onSuccessCallback: (commit, data) => {
          
        },
        errorMessage: "Error importando los litros del Excel",
      });
    },

    async verificarControlLecheroMes({ dispatch }, mes) {
      if (!mes) return { data: { cargado: false } };
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(
            `/grilla_vaca/upload-excel/check_mes?mes=${encodeURIComponent(mes)}`
          ),
        errorMessage: "Error verificando control lechero del mes",
      });
    },

    async getPendienteById(
      { dispatch },
      { animalId, skipRecalculo = true, categoria = null } = {}
    ) {
      let url = `grilla_vaca/preview_pendientes/${animalId}?skip_recalculo=${
        skipRecalculo ? "true" : "false"
      }`;
      if (categoria) {
        url += `&categoria=${categoria}`;
      }
      return await dispatch("executeApiCall", {
        apiCallFunction: () => apiService.getCustom(url),
        onSuccessCallback: (commit, response) => {
          
          return response;
        },
        errorMessage: "Error obteniendo preview de pendiente",
      });
    },

    async ejecutarAccionPendiente(
      { dispatch, commit },
      { animalId, payload = null }
    ) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(
            `grilla_vaca/ejecutar_pendientes/${animalId}`,
            payload
          ),
        onSuccessCallback: async (commit, response) => {
          
          // Invalidar cache: la acción cambió los flags del animal
          commit("invalidatePendientesCache");
          try {
            await dispatch("getPendientesCambio", { pagina: 1, limite: 10 });
          } catch (error) {
            
          }
        },
        errorMessage: "Error ejecutando acción pendiente",
      });
    },

    async actualizarPendientesEnBackground({ dispatch, commit }) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom("grilla_vaca/pendientes/actualizar", {}),
        onSuccessCallback: (commit, response) => {
          // Invalidar cache para forzar recarga en próxima consulta
          commit("invalidatePendientesCache");
          
        },
        errorMessage: "Error iniciando actualización de pendientes",
      });
    },

    // ============================================
    // VACUNACIÓN
    // ============================================

    async getVacunasChecklist({ dispatch }, animalId) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(`grilla_vaca/vacunas/checklist/${animalId}`),
        onSuccessCallback: (commit, response) => {
          
          return response;
        },
        errorMessage: "Error obteniendo checklist de vacunas",
      });
    },

    async toggleVacuna(
      { dispatch },
      {
        animalId,
        vacunaId = null,
        nombre_vacuna = null,
        empleadoId = null,
        productoId = null,
        fecha_aplicacion = null,
      }
    ) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(`grilla_vaca/vacunas/aplicar/${animalId}`, {
            vacuna_id: vacunaId,
            nombre_vacuna: nombre_vacuna,
            empleado_id: empleadoId,
            producto_id: productoId,
            fecha_aplicacion: fecha_aplicacion,
          }),
        onSuccessCallback: (commit, response) => {
          
          return response;
        },
        errorMessage: "Error al aplicar/desmarcar vacuna",
      });
    },

    // ============================================
    // TACTOS
    // ============================================

    async getTactosByAnimalId({ dispatch }, animalId) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(`grilla_vaca/tactos/${animalId}`),
        onSuccessCallback: (commit, response) => {
          
          return response;
        },
        errorMessage: "Error obteniendo historial de tactos",
      });
    },

    async postTacto({ dispatch, commit }, { animalId, payload }) {
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(`/grilla_vaca/tacto/${animalId}`, payload),
        onSuccessCallback: async (commit) => {
          commit("invalidatePendientesCache");
          await dispatch("getVacas");
        },
        errorMessage: "Error al registrar tacto",
      });
    },

    async getMuertos({ dispatch }, { pagina = 1, limite = 100 } = {}) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(
            `grilla_vaca?estado_excepcion=muerto&pagina=${pagina}&limite=${limite}`
          ),
        onSuccessCallback: (commit, response) => {
          const {
            datos,
            total,
            pagina_actual,
            total_paginas,
            limite: lim,
            siguiente_pagina,
            anterior_pagina,
          } = response;
          commit("setAnimalesMuertos", datos || []);
          commit("setPaginacionMuertos", {
            total: total || 0,
            pagina_actual: pagina_actual || 1,
            total_paginas: total_paginas || 1,
            limite: lim || 100,
            siguiente_pagina: siguiente_pagina || null,
            anterior_pagina: anterior_pagina || null,
          });
        },
        errorMessage: "Error obteniendo animales muertos",
      });
    },

    async getAbortos({ dispatch }, { pagina = 1, limite = 100 } = {}) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(
            `grilla_vaca?estado_excepcion=aborto&pagina=${pagina}&limite=${limite}`
          ),
        onSuccessCallback: (commit, response) => {
          const {
            datos,
            total,
            pagina_actual,
            total_paginas,
            limite: lim,
            siguiente_pagina,
            anterior_pagina,
          } = response;
          commit("setAnimalesAbortos", datos || []);
          commit("setPaginacionAbortos", {
            total: total || 0,
            pagina_actual: pagina_actual || 1,
            total_paginas: total_paginas || 1,
            limite: lim || 100,
            siguiente_pagina: siguiente_pagina || null,
            anterior_pagina: anterior_pagina || null,
          });
        },
        errorMessage: "Error obteniendo animales abortados",
      });
    },

    async registrarAborto({ dispatch, commit }, { animalId, payload }) {
      if (!navigator.onLine) {
        await enqueueOp(
          "animales",
          "update",
          payload || {},
          `/grilla_vaca/aborto/${animalId}`,
          "POST"
        );
        const local = await db.animales.where("id").equals(animalId).first();
        if (local) {
          await db.animales
            .update(local.localId, {
              cantidad_abortos: (local.cantidad_abortos || 0) + 1,
              prenada: false,
            })
            .catch(() => {});
        }
        commit("invalidatePendientesCache");
        await dispatch("getVacas");
        return {
          status: 200,
          data: { detail: "Aborto registrado offline", _offline: true },
        };
      }
      return dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(`/grilla_vaca/aborto/${animalId}`, payload),
        onSuccessCallback: async (commit) => {
          commit("invalidatePendientesCache");
          await dispatch("getVacas");
        },
        errorMessage: "Error al registrar aborto",
      });
    },

    guardarCriaPartoPendiente({ commit }, data) {
      commit("setCriaPartoPendiente", data);
    },

    limpiarCriaPartoPendiente({ commit }) {
      commit("setCriaPartoPendiente", null);
    },

    async exportarAnimalesTambo({ dispatch }) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom("exportaciones/animales-tambo", {
            responseType: "blob",
          }),
        errorMessage: "Error al exportar animales a Excel",
      });
    },

    async fetchConteoEnfermas({ dispatch }) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () => apiService.getCustom("vacas/enfermas/conteo"),
        errorMessage: "Error al obtener conteo de enfermas",
      });
    },

    async fetchResumenAnimales({ dispatch }, query = "pagina=1&limite=100") {
      const queryString = typeof query === "string" ? query : "pagina=1&limite=100";
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(`grilla_vaca/resumen?${queryString}`),
        errorMessage: "Error al obtener resumen de animales",
      });
    },

    async fetchCabezasActivasTotal({ dispatch }, url = "/tambos/cabezas-activas-total") {
      const targetUrl =
        typeof url === "string" ? url : url?.url || "/tambos/cabezas-activas-total";
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(targetUrl),
        errorMessage: "Error al obtener total de cabezas activas",
      });
    },

    async fetchTorosDisponibles({ dispatch }) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () => apiService.getCustom("/cria/toros"),
        errorMessage: "Error al cargar toros disponibles",
      });
    },

    async fetchActividadesDiarias({ dispatch }, payload = {}) {
      const url =
        payload.url ||
        (payload.fecha !== undefined
          ? "/rodeo/actividades-diarias"
          : "/grilla_vaca/actividades-realizadas");
      const params = payload.params || payload;
      return await dispatch("executeApiCall", {
        apiCallFunction: () => apiService.getCustom(url, { params }),
        errorMessage: "Error al obtener actividades diarias",
      });
    },

    async postDesteteCria({ dispatch }, payload) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () => apiService.postCustom("/cria/destete", payload),
        errorMessage: "Error al registrar destete de cría",
      });
    },

    /**
     * Evaluación de una campaña IATF. Sin `estacion`/`anio` el backend evalúa la campaña
     * de prioridad; ambos van juntos (422 si falta uno).
     * @param {{ estacion?: string, anio?: number }} [filtros]
     */
    async fetchUltimaEvaluacionIATF({ dispatch }, { estacion, anio } = {}) {
      const params = {};
      if (estacion) params.estacion = estacion;
      if (anio) params.anio = anio;
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom("/cria/iatf/evaluacion-ultima", { params }),
        errorMessage: "Error al cargar evaluación IATF",
      });
    },

    async postObservacionVisual({ dispatch }, payload) {
      const url =
        payload.url ||
        (payload.caravana !== undefined
          ? "/grilla-vaca/observacion-visual"
          : "/cria/observaciones-visuales");
      return await dispatch("executeApiCall", {
        apiCallFunction: () => apiService.postCustom(url, payload),
        errorMessage: "Error al registrar observación visual",
      });
    },

    async postTactoPersonalizado({ dispatch }, payload) {
      const url =
        payload.url ||
        (payload.tactos !== undefined
          ? "/grilla_vaca/tacto/personalizado"
          : "/cria/tactos/personalizado");
      if (!navigator.onLine) {
        await enqueueOp("tactos", "create", payload, url, "POST");
        return {
          status: 200,
          data: { detail: "Tacto guardado offline", _offline: true },
        };
      }
      return await dispatch("executeApiCall", {
        apiCallFunction: () => apiService.postCustom(url, payload),
        errorMessage: "Error al registrar tacto personalizado",
      });
    },

    async fetchTratamientosAnimal({ dispatch }, animalId) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(`/veterinario/tratamientos/animal/${animalId}`),
        errorMessage: "Error al obtener tratamientos del animal",
      });
    },

    async finalizarTratamientoAnimal({ dispatch }, payload) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom("/veterinario/tratamientos/finalizar", payload),
        errorMessage: "Error al finalizar tratamiento",
      });
    },

    async eliminarTratamientoAnimal({ dispatch }, tratamientoId) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.deleteCustom(`veterinario/tratamientos/${tratamientoId}`),
        errorMessage: "Error al eliminar tratamiento",
      });
    },

    async fetchTratamientosGrillaVaca({ dispatch }, animalId) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.getCustom(`grilla_vaca/${animalId}/tratamientos`),
        errorMessage: "Error al obtener tratamientos del animal",
      });
    },

    async postTratamientoGrillaVaca({ dispatch }, { animalId, payload }) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.postCustom(`grilla_vaca/${animalId}/tratamientos`, payload),
        errorMessage: "Error al registrar tratamiento",
      });
    },

    async deleteTratamientoGrillaVaca({ dispatch }, { animalId, tratamientoId }) {
      return await dispatch("executeApiCall", {
        apiCallFunction: () =>
          apiService.delete(`grilla_vaca/${animalId}/tratamientos`, tratamientoId),
        errorMessage: "Error al eliminar tratamiento",
      });
    },
  },
};

